'use client';

import './giving-history.scss';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { Select } from '@sola/ark-core/molecules/select/select.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import {
  useGivingHistory,
  useReadyForQueries,
  useSupporterQuery,
} from '@sola/supporter-sdk/hooks.js';
import { commitmentsQuery } from '@sola/supporter-sdk/queries.js';

import { CTA_CONTACT_SUPPORT } from '../../utils/ctas.ts';
import { GivingHistoryMonth } from './giving-history-month.tsx';
import {
  attachCommitmentToGivingItem,
  formatGivingItemsGroup,
  getYearList,
  groupGivingItemsByMonth,
} from './utils.ts';

/**
 * Get component data
 */
export const useComponentData = () => {
  // @ts-expect-error //TODO This should be fixed once we tighten up useSupporterQuery types.
  const { data: commitments }: { data: GraphQLCommitment[] | undefined } =
    useSupporterQuery(commitmentsQuery);

  const { t } = useTranslation('my');
  const { isReady } = useReadyForQueries();

  const [year, setYear] = useState(new Date().getFullYear());
  const {
    data: givingHistoryItems,
    isLoading,
    isFetching,
    isPending,
    isError,
  } = useGivingHistory(year);
  /** NOTE: Could be configured in CMS */
  const numYearsToDisplay = 7;

  /**
   * Handle year change.
   */
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(Number(e.target.value));
  };

  /**
   * Group and format items for rendering.
   */
  const groupedItems = useMemo(() => {
    if (!givingHistoryItems?.length || !commitments?.length) {
      return null;
    }

    const mappedGivingHistoryItems = givingHistoryItems.map(
      attachCommitmentToGivingItem(commitments),
    );
    const grouped =
      mappedGivingHistoryItems.length > 0
        ? formatGivingItemsGroup(
            givingHistoryItems[0].taxYear,
            groupGivingItemsByMonth(mappedGivingHistoryItems),
            t,
          )
            .toReversed()
            .filter((group) => group.givingHistoryItems.length)
        : [];

    return grouped;
    // We want to run logic either when item date change or when commitments initially load.
  }, [givingHistoryItems?.[0]?.recordDate, !!commitments?.length]);

  const hasQueryResults = !(isLoading || isFetching || isPending) || isError;

  return {
    isShowingSkeletons: !isReady || isFetching || !hasQueryResults,
    yearList: getYearList(new Date(), numYearsToDisplay),
    hasData: !!givingHistoryItems?.length && groupedItems,
    hasError: isError,
    groupedItems,
    handleYearChange,
  };
};

/**
 * Displays list of commitments based on frequency.
 */
export const GivingHistory = () => {
  const { t } = useTranslation('my');

  const {
    isShowingSkeletons,
    hasData,
    hasError,
    groupedItems,
    yearList,
    handleYearChange,
  } = useComponentData();

  return (
    <div className="max-w-md">
      <div className="flex flex-col gap-snug-lg mb-comfy-xl">
        <div className="GivingHistory__yearselect">
          <Select
            label={t('givingHistory.title')}
            items={yearList.map((y) => {
              return {
                value: y.toString(),
              };
            })}
            onChange={handleYearChange}
            disabled={isShowingSkeletons}
          />
        </div>
      </div>

      {isShowingSkeletons && <GivingHistoryMonth isLoading />}

      {!isShowingSkeletons &&
        hasData &&
        groupedItems!.map((givingMonthGroup) => (
          <GivingHistoryMonth
            key={givingMonthGroup.heading}
            {...givingMonthGroup}
          />
        ))}

      {!isShowingSkeletons && !hasData && !hasError && (
        <Alert message={t('givingHistory.noHistory')} />
      )}

      {!isShowingSkeletons && hasError && (
        <Alert
          title={t('givingHistory.requestError.title')}
          message={t('givingHistory.requestError.message')}
          variant="negative"
          action={
            <Link href={CTA_CONTACT_SUPPORT.url}>
              {t(CTA_CONTACT_SUPPORT.translation)}
            </Link>
          }
        />
      )}
    </div>
  );
};
